// client/src/components/watchdog/WatchdogOrderTable.jsx
import React, { useState, useEffect, useRef } from "react";
import { formatEasternDateTime } from "../../utils/timezone";

const STATUS_COLORS = {
    event_received: { bg: "#f59e0b20", text: "#f59e0b", label: "Received" },
    queued: { bg: "#f59e0b20", text: "#f59e0b", label: "Queued" },
    validating: { bg: "#06b6d420", text: "#06b6d4", label: "Validating..." },
    validated_ok: { bg: "#10b98120", text: "#10b981", label: "OK" },
    validated_mismatch: { bg: "#ef444420", text: "#ef4444", label: "MISMATCH" },
    action_pending: { bg: "#f59e0b20", text: "#f59e0b", label: "Action Pending" },
    action_settle: { bg: "#a855f720", text: "#a855f7", label: "Settle Window" },
    action_retry: { bg: "#f9731620", text: "#f97316", label: "Retrying Action" },
    validation_retry: { bg: "#f9731620", text: "#f97316", label: "Awaiting ShipHero" },
    action_taken: { bg: "#22c55e20", text: "#22c55e", label: "Cancelled" },
    action_failed: { bg: "#ef444420", text: "#ef4444", label: "Failed" },
    dismissed: { bg: "#94a3b820", text: "#94a3b8", label: "Dismissed" },
    resolved_by_shiphero: { bg: "#06b6d420", text: "#06b6d4", label: "SH Resolved" },
    fulfilled: { bg: "#6366f120", text: "#6366f1", label: "Fulfilled" },
    cancelled_in_shopify: { bg: "#94a3b820", text: "#94a3b8", label: "Shopify Cancelled" },
};

// Countdown to the next processor pickup (settle expiry / retry due time)
function nextCheckLabel(order) {
    if (!order?.next_retry_at) return null;
    const inFlight = ["action_settle", "action_retry", "validation_retry"].includes(order.status);
    if (!inFlight) return null;
    const diff = new Date(order.next_retry_at).getTime() - Date.now();
    if (diff <= 0) return "due now";
    if (diff < 60000) return `in ${Math.ceil(diff / 1000)}s`;
    return `in ${Math.ceil(diff / 60000)}m`;
}

const s = {
    container: {
        background: "#1a1f2e",
        borderRadius: "12px",
        border: "1px solid #1e293b",
        overflow: "hidden",
        marginBottom: "20px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 20px",
        borderBottom: "1px solid #1e293b",
    },
    title: { color: "#e2e8f0", fontSize: "14px", fontWeight: 700 },
    filters: { display: "flex", gap: "8px", alignItems: "center" },
    filterBtn: (active) => ({
        background: active ? "#6366f1" : "transparent",
        color: active ? "#fff" : "#94a3b8",
        border: active ? "none" : "1px solid #334155",
        borderRadius: "6px",
        padding: "4px 12px",
        fontSize: "11px",
        fontWeight: 600,
        cursor: "pointer",
    }),
    searchInput: {
        background: "#0f1219",
        border: "1px solid #334155",
        borderRadius: "6px",
        color: "#e2e8f0",
        padding: "4px 10px",
        fontSize: "12px",
        width: "160px",
    },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
        padding: "10px 16px",
        color: "#64748b",
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        textAlign: "left",
        borderBottom: "1px solid #1e293b",
        background: "#151921",
    },
    td: {
        padding: "10px 16px",
        color: "#e2e8f0",
        fontSize: "13px",
        borderBottom: "1px solid #1e293b22",
    },
    badge: (colors) => ({
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 700,
        background: colors.bg,
        color: colors.text,
    }),
    actionBtn: (color) => ({
        background: `${color}20`,
        color: color,
        border: `1px solid ${color}44`,
        borderRadius: "5px",
        padding: "3px 10px",
        fontSize: "11px",
        fontWeight: 600,
        cursor: "pointer",
        marginRight: "4px",
    }),
    pagination: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "12px",
        borderTop: "1px solid #1e293b",
    },
    pageBtn: (disabled) => ({
        background: disabled ? "#1e293b" : "#334155",
        color: disabled ? "#64748b" : "#e2e8f0",
        border: "none",
        borderRadius: "4px",
        padding: "4px 12px",
        fontSize: "12px",
        cursor: disabled ? "default" : "pointer",
    }),
    expandRow: {
        background: "#0f121966",
    },
    empty: {
        padding: "40px",
        textAlign: "center",
        color: "#64748b",
        fontSize: "13px",
    },
    inlineError: {
        padding: "4px 12px",
        background: "rgba(239, 68, 68, 0.08)",
        borderRadius: "4px",
        color: "#fca5a5",
        fontSize: "11px",
        marginTop: "2px",
    },
};

// ─── Detail Panel Styles ─────────────────────────────────────────────────────
const detail = {
    grid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "16px",
        padding: "16px 20px",
    },
    col: {
        background: "#0f1219",
        borderRadius: "8px",
        padding: "14px 16px",
        border: "1px solid #1e293b",
    },
    colTitle: {
        fontSize: "11px",
        fontWeight: 700,
        color: "#8b5cf6",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: "10px",
        paddingBottom: "6px",
        borderBottom: "1px solid #1e293b",
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "3px 0",
        fontSize: "12px",
    },
    label: {
        color: "#64748b",
        fontWeight: 500,
    },
    value: {
        color: "#e2e8f0",
        fontWeight: 600,
        textAlign: "right",
        maxWidth: "60%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    errorValue: {
        color: "#fca5a5",
        fontWeight: 600,
        fontSize: "11px",
        textAlign: "right",
        maxWidth: "60%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    successValue: {
        color: "#6ee7b7",
        fontWeight: 600,
    },
    failValue: {
        color: "#fca5a5",
        fontWeight: 600,
    },
    subTable: {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: "12px",
    },
    subTh: {
        padding: "6px 12px",
        color: "#64748b",
        fontSize: "10px",
        fontWeight: 600,
        textTransform: "uppercase",
        textAlign: "left",
        borderBottom: "1px solid #1e293b44",
    },
    subTd: (mismatch) => ({
        padding: "6px 12px",
        color: mismatch ? "#ef4444" : "#94a3b8",
        fontSize: "12px",
        borderBottom: "1px solid #1e293b22",
        background: mismatch ? "#ef444408" : "transparent",
    }),
};

const FILTER_TABS = [
    { key: "all", label: "All" },
    { key: "action_pending", label: "⏳ Action Pending" },
    { key: "pending", label: "Pending" },
    { key: "mismatch", label: "Mismatches" },
    { key: "resolved", label: "Resolved" },
    { key: "failed", label: "Failed" },
    { key: "archived", label: "🗄 Archived" },
];

function timeAgo(dateStr) {
    if (!dateStr) return "\u2014";
    const diff = Date.now() - new Date(dateStr).getTime();
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
}

function formatDateTime(dateStr) {
    return formatEasternDateTime(dateStr, {
        month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false,
    });
}

function statusIcon(val) {
    if (val === "success") return "\u2705";
    if (val === "failed") return "\u274c";
    if (val === "skipped") return "\u23ed\ufe0f";
    return "\u2014";
}

// ─── 3-Column Order Detail Panel ─────────────────────────────────────────────
function OrderDetailPanel({ order }) {
    const triggerSummary = order.trigger_summary || {};

    return (
        <div style={detail.grid}>
            {/* Col 1: Trigger Context */}
            <div style={detail.col}>
                <div style={detail.colTitle}>Trigger Context</div>
                <div style={detail.row}>
                    <span style={detail.label}>Topic</span>
                    <span style={detail.value}>{order.trigger_topic || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Trigger Count</span>
                    <span style={detail.value}>{order.trigger_count || 1}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>First Event</span>
                    <span style={detail.value}>{formatDateTime(order.first_event_at)}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Last Event</span>
                    <span style={detail.value}>{formatDateTime(order.last_event_at)}</span>
                </div>
                {order.original_location_name && (
                    <div style={detail.row}>
                        <span style={detail.label}>From Location</span>
                        <span style={detail.value} title={order.original_location_id}>{order.original_location_name}</span>
                    </div>
                )}
                {order.new_location_name && (
                    <div style={detail.row}>
                        <span style={detail.label}>To Location</span>
                        <span style={detail.value} title={order.new_location_id}>{order.new_location_name}</span>
                    </div>
                )}
                {Object.keys(triggerSummary).length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                        <div style={{ ...detail.label, marginBottom: "4px" }}>Summary</div>
                        <pre style={{
                            color: "#94a3b8", fontSize: "10px", margin: 0,
                            background: "#0a0d14", padding: "6px 8px", borderRadius: "4px",
                            overflow: "auto", maxHeight: "100px", whiteSpace: "pre-wrap",
                        }}>
                            {JSON.stringify(triggerSummary, null, 2)}
                        </pre>
                    </div>
                )}
            </div>

            {/* Col 2: Validation */}
            <div style={detail.col}>
                <div style={detail.colTitle}>Validation</div>
                <div style={detail.row}>
                    <span style={detail.label}>Checked At</span>
                    <span style={detail.value}>{formatDateTime(order.validation_checked_at)}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Shopify Found</span>
                    <span style={order.shopify_found ? detail.successValue : detail.failValue}>
                        {order.shopify_found ? "Yes" : order.shopify_found === false ? "No" : "\u2014"}
                    </span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Assigned Location</span>
                    <span style={detail.value}>{order.assigned_location_name || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Expected Location</span>
                    <span style={detail.value} title={order.expected_location_id}>
                        {order.expected_location_id ? `ID: ${order.expected_location_id.substring(0, 12)}...` : "\u2014"}
                    </span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Mismatch</span>
                    <span style={order.location_mismatch ? detail.failValue : detail.successValue}>
                        {order.location_mismatch ? "YES" : "No"}
                    </span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Shopify Status</span>
                    <span style={detail.value}>
                        {order.shopify_fulfillment_status || "\u2014"}
                        {order.shopify_financial_status ? ` \u00b7 ${order.shopify_financial_status}` : ""}
                    </span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>ShipHero Status</span>
                    <span style={{
                        ...detail.value,
                        color: order.shiphero_status === "not_found" ? "#f59e0b"
                             : order.shiphero_status === "cancelled" ? "#fca5a5"
                             : order.shiphero_status === "fulfilled" ? "#6ee7b7"
                             : "#e2e8f0",
                    }}>
                        {order.shiphero_status || "\u2014"}
                        {order.shiphero_fulfillment_status && order.shiphero_fulfillment_status !== order.shiphero_status
                            ? ` (${order.shiphero_fulfillment_status})` : ""}
                    </span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Retry Count</span>
                    <span style={{
                        ...detail.value,
                        color: (order.retry_count || 0) > 0 ? "#f59e0b" : "#e2e8f0",
                    }}>
                        {order.retry_count || 0}
                    </span>
                </div>
                {order.validation_error && (
                    <div style={{ marginTop: "6px" }}>
                        <div style={{ ...detail.label, color: "#fca5a5", marginBottom: "2px" }}>Validation Error</div>
                        <div style={detail.errorValue}>{order.validation_error}</div>
                    </div>
                )}
            </div>

            {/* Col 3: Action Breakdown */}
            <div style={detail.col}>
                <div style={detail.colTitle}>Action Breakdown</div>
                <div style={detail.row}>
                    <span style={detail.label}>Mode</span>
                    <span style={detail.value}>{order.action_mode || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Cancel</span>
                    <span style={detail.value}>{statusIcon(order.cancel_status)} {order.cancel_status || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Tag</span>
                    <span style={detail.value}>{statusIcon(order.tag_status)} {order.tag_status || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>History</span>
                    <span style={detail.value}>{statusIcon(order.history_status)} {order.history_status || "\u2014"}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>Acted At</span>
                    <span style={detail.value}>{formatDateTime(order.action_taken_at)}</span>
                </div>
                <div style={detail.row}>
                    <span style={detail.label}>SH Self-Cancelled</span>
                    <span style={detail.value}>{order.shiphero_self_cancelled ? "Yes" : "No"}</span>
                </div>
                {order.action_error && (
                    <div style={{ marginTop: "6px" }}>
                        <div style={{ ...detail.label, color: "#fca5a5", marginBottom: "2px" }}>Action Error</div>
                        <div style={detail.errorValue}>{order.action_error}</div>
                    </div>
                )}
                {order.notes && (
                    <div style={{ marginTop: "6px" }}>
                        <div style={{ ...detail.label, marginBottom: "2px" }}>Notes</div>
                        <div style={{ color: "#94a3b8", fontSize: "11px" }}>{order.notes}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── SH Line-Item Status Badge ───────────────────────────────────────────────
function ShStatusBadge({ status }) {
    if (!status) return <span style={{ color: "#64748b" }}>{"\u2014"}</span>;
    const s = status.toLowerCase();
    const color =
        s === "pending" ? "#06b6d4"
        : s === "fulfilled" || s === "shipped" ? "#10b981"
        : s === "cancelled" || s === "canceled" ? "#ef4444"
        : s === "partially_fulfilled" ? "#8b5cf6"
        : "#94a3b8";
    return (
        <span style={{
            display: "inline-block", padding: "1px 6px", borderRadius: "3px",
            fontSize: "10px", fontWeight: 700, background: `${color}20`, color,
        }}>
            {status}
        </span>
    );
}

// ─── Line Item Result Badge ──────────────────────────────────────────────────
function ResultBadge({ li, wasCancelled, orderStatus }) {
    if (li.skip_reason === "already_cancelled") {
        return <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 700 }}>ALREADY CANCELLED</span>;
    }
    if (li.skip_reason) {
        return <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 700 }}>SKIPPED</span>;
    }
    if (!li.should_cancel) {
        return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 700, background: "#10b98120", color: "#10b981" }}>KEEP</span>;
    }
    // should_cancel = true
    if (wasCancelled) {
        return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 700, background: "#22c55e20", color: "#22c55e" }}>{"\u2705"} CANCELLED</span>;
    }
    if (orderStatus === "action_taken") {
        // Action was taken but this item wasn't in cancelled list — probably OK
        return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 700, background: "#22c55e20", color: "#22c55e" }}>{"\u2705"} CANCELLED</span>;
    }
    if (orderStatus === "action_failed") {
        return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 700, background: "#ef444420", color: "#ef4444" }}>{"\u274c"} FAILED</span>;
    }
    // Pending action
    return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 700, background: "#ef444420", color: "#ef4444" }}>CANCEL</span>;
}

// ─── Line Item Sub-table ─────────────────────────────────────────────────────
function LineItemSubTable({ lineItems, cancelledIds, orderStatus }) {
    if (!lineItems || lineItems.length === 0) return null;
    const cancelledSet = new Set(cancelledIds || []);

    return (
        <table style={detail.subTable}>
            <thead>
                <tr>
                    <th style={detail.subTh}>SKU</th>
                    <th style={detail.subTh}>Product</th>
                    <th style={detail.subTh}>Qty</th>
                    <th style={detail.subTh}>SH Fulfillment</th>
                    <th style={detail.subTh}>Location</th>
                    <th style={detail.subTh}>Mismatch</th>
                    <th style={detail.subTh}>Result</th>
                </tr>
            </thead>
            <tbody>
                {lineItems.map((li, i) => {
                    const wasCancelled = cancelledSet.has(li.shiphero_line_item_id);
                    return (
                        <tr key={i}>
                            <td style={detail.subTd(li.should_cancel)}>
                                <span style={{ fontFamily: "monospace", fontSize: "11px" }}>{li.sku || "\u2014"}</span>
                            </td>
                            <td style={detail.subTd(li.should_cancel)} title={li.product_name}>
                                {(li.product_name || "").substring(0, 50)}
                            </td>
                            <td style={{ ...detail.subTd(li.should_cancel), textAlign: "center" }}>{li.quantity}</td>
                            <td style={detail.subTd(li.should_cancel)}>
                                <ShStatusBadge status={li.shiphero_fulfillment_status} />
                            </td>
                            <td style={detail.subTd(li.should_cancel)}>
                                <span style={{ fontWeight: li.location_mismatch ? 700 : 400 }}>
                                    {li.assigned_location_name || "\u2014"}
                                </span>
                            </td>
                            <td style={{ ...detail.subTd(li.should_cancel), textAlign: "center" }}>
                                {li.skip_reason
                                    ? <span style={{ color: "#94a3b8", fontSize: "10px" }}>{li.skip_reason}</span>
                                    : li.location_mismatch
                                        ? <span style={{ color: "#ef4444", fontWeight: 700 }}>YES</span>
                                        : <span style={{ color: "#10b981" }}>No</span>
                                }
                            </td>
                            <td style={detail.subTd(li.should_cancel)}>
                                <ResultBadge li={li} wasCancelled={wasCancelled} orderStatus={orderStatus} />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// ─── Checkbox Style ──────────────────────────────────────────────────────────
const checkboxStyle = {
    width: "16px",
    height: "16px",
    accentColor: "#8b5cf6",
    cursor: "pointer",
};

const bulkBarStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 20px",
    background: "linear-gradient(135deg, #1e1b4b 0%, #1a1f2e 100%)",
    borderBottom: "1px solid #6366f144",
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function WatchdogOrderTable({
    orders, total, pages, page, setPage,
    filters, setFilters, loading,
    onExecute, onDismiss, onRevalidate, onBulkDismiss, actionLoading,
    actionError,
}) {
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Debounce search: auto-apply after 400ms of no typing
    const debounceRef = useRef(null);
    useEffect(() => {
        debounceRef.current = setTimeout(() => {
            setPage(1);
            setFilters(f => {
                if (f.search === search) return f;
                return { ...f, search };
            });
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            clearTimeout(debounceRef.current);
            setPage(1);
            setFilters(f => ({ ...f, search }));
        }
    };

    // ─── Selection helpers ────────────────────────────────────────────────
    const dismissableStatuses = new Set([
        "validated_ok", "validated_mismatch", "action_taken", "action_failed",
        "resolved_by_shiphero", "fulfilled", "cancelled_in_shopify", "event_received", "queued",
    ]);
    const dismissableOrders = orders.filter(o => dismissableStatuses.has(o.status) && o.status !== "dismissed");
    const allSelected = dismissableOrders.length > 0 && dismissableOrders.every(o => selectedIds.has(o.id));
    const someSelected = selectedIds.size > 0;

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(dismissableOrders.map(o => o.id)));
        }
    };

    const handleBulkDismiss = () => {
        if (selectedIds.size === 0 || !onBulkDismiss) return;
        onBulkDismiss([...selectedIds]);
        setSelectedIds(new Set());
    };

    return (
        <div style={s.container}>
            <div style={s.header}>
                <div style={s.title}>
                    Watched Orders {total > 0 && <span style={{ color: "#64748b", fontWeight: 400 }}>({total})</span>}
                </div>
                <div style={s.filters}>
                    {FILTER_TABS.map(f => (
                        <button
                            key={f.key}
                            style={s.filterBtn(filters.status === f.key)}
                            onClick={() => { setPage(1); setFilters(prev => ({ ...prev, status: f.key })); }}
                        >
                            {f.label}
                        </button>
                    ))}
                    <input
                        style={s.searchInput}
                        placeholder="Search order #..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={handleSearch}
                    />
                </div>
            </div>

            {/* ─── Bulk Action Bar ──────────────────────────────── */}
            {someSelected && (
                <div style={bulkBarStyle}>
                    <span style={{ color: "#c4b5fd", fontSize: "13px", fontWeight: 600 }}>
                        {selectedIds.size} selected
                    </span>
                    <button
                        style={{
                            background: "#94a3b820",
                            color: "#94a3b8",
                            border: "1px solid #94a3b844",
                            borderRadius: "6px",
                            padding: "5px 14px",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: actionLoading === "bulk-dismiss" ? "not-allowed" : "pointer",
                            opacity: actionLoading === "bulk-dismiss" ? 0.5 : 1,
                        }}
                        onClick={handleBulkDismiss}
                        disabled={actionLoading === "bulk-dismiss"}
                    >
                        {actionLoading === "bulk-dismiss" ? "Dismissing..." : `Dismiss ${selectedIds.size} order(s)`}
                    </button>
                    <button
                        style={{
                            background: "transparent",
                            color: "#64748b",
                            border: "1px solid #334155",
                            borderRadius: "6px",
                            padding: "5px 10px",
                            fontSize: "11px",
                            cursor: "pointer",
                        }}
                        onClick={() => setSelectedIds(new Set())}
                    >
                        Clear selection
                    </button>
                </div>
            )}

            {orders.length === 0 ? (
                <div style={s.empty}>
                    {loading ? "Loading..." : "No watched orders yet. Webhook events will appear here automatically."}
                </div>
            ) : (
                <>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={{ ...s.th, width: "36px", textAlign: "center" }}>
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        style={checkboxStyle}
                                        title="Select all dismissable orders on this page"
                                    />
                                </th>
                                <th style={s.th}></th>
                                <th style={s.th}>Order</th>
                                <th style={s.th}>Trigger</th>
                                <th style={s.th}>Status</th>
                                <th style={s.th}>SH Status</th>
                                <th style={s.th}>Location</th>
                                <th style={s.th}>Events</th>
                                <th style={s.th}>Last Event</th>
                                <th style={s.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(order => {
                                const expanded = expandedIds.has(order.id);
                                const statusInfo = STATUS_COLORS[order.status] || STATUS_COLORS.event_received;
                                const lineItems = order.line_item_validation || [];
                                const hasInlineError = actionError?.orderId === order.id;

                                return (
                                    <React.Fragment key={order.id}>
                                        <tr
                                            style={{ cursor: "pointer" }}
                                            onClick={() => toggleExpand(order.id)}
                                        >
                                            <td style={{ ...s.td, width: "36px", textAlign: "center" }} onClick={e => e.stopPropagation()}>
                                                {dismissableStatuses.has(order.status) && order.status !== "dismissed" ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(order.id)}
                                                        onChange={() => toggleSelect(order.id)}
                                                        style={checkboxStyle}
                                                    />
                                                ) : null}
                                            </td>
                                            <td style={{ ...s.td, width: "30px", textAlign: "center" }}>
                                                {expanded ? "\u25BC" : "\u25B6"}
                                            </td>
                                            <td style={{ ...s.td, fontWeight: 600 }}>{order.order_number}</td>
                                            <td style={s.td}>
                                                <span style={{ color: "#8b5cf6", fontSize: "12px" }}>
                                                    {(order.trigger_topic || "").replace("fulfillment_orders/", "FO/")}
                                                </span>
                                            </td>
                                            <td style={s.td}>
                                                <span style={s.badge(statusInfo)}>{statusInfo.label}</span>
                                                {nextCheckLabel(order) && (
                                                    <span style={{ marginLeft: 6, fontSize: "10px", color: "#94a3b8" }}>
                                                        next check {nextCheckLabel(order)}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={s.td}>
                                                {order.shiphero_status ? (
                                                    <span style={{
                                                        display: "inline-block",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        background: order.shiphero_status === "fulfilled" ? "#10b98120"
                                                                  : order.shiphero_status === "cancelled" ? "#ef444420"
                                                                  : order.shiphero_status === "not_found" ? "#f59e0b20"
                                                                  : order.shiphero_status === "pending" ? "#06b6d420"
                                                                  : order.shiphero_status === "partial" ? "#8b5cf620"
                                                                  : "#94a3b820",
                                                        color: order.shiphero_status === "fulfilled" ? "#10b981"
                                                             : order.shiphero_status === "cancelled" ? "#ef4444"
                                                             : order.shiphero_status === "not_found" ? "#f59e0b"
                                                             : order.shiphero_status === "pending" ? "#06b6d4"
                                                             : order.shiphero_status === "partial" ? "#8b5cf6"
                                                             : "#94a3b8",
                                                    }}>
                                                        {order.shiphero_status}
                                                    </span>
                                                ) : <span style={{ color: "#64748b" }}>{"\u2014"}</span>}
                                            </td>
                                            <td style={s.td}>
                                                {(() => {
                                                    const liItems = order.line_item_validation || [];
                                                    const mismatched = liItems.filter(li => li.location_mismatch || li.should_cancel);
                                                    if (mismatched.length > 0) {
                                                        // Show mismatched location(s) prominently
                                                        const uniqueLocs = [...new Set(mismatched.map(li => li.assigned_location_name).filter(Boolean))];
                                                        const okCount = liItems.length - mismatched.length;
                                                        return (
                                                            <div style={{ lineHeight: "1.4" }}>
                                                                {uniqueLocs.map((loc, i) => (
                                                                    <span key={i} style={{
                                                                        display: "inline-block", padding: "1px 6px",
                                                                        borderRadius: "3px", fontSize: "11px", fontWeight: 700,
                                                                        background: "#ef444418", color: "#ef4444",
                                                                        marginRight: "4px",
                                                                    }}>
                                                                        {"\u26A0"} {loc}
                                                                    </span>
                                                                ))}
                                                                {okCount > 0 && (
                                                                    <span style={{ color: "#64748b", fontSize: "10px" }}>
                                                                        +{okCount} ok
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return order.assigned_location_name || order.new_location_name || "\u2014";
                                                })()}
                                            </td>
                                            <td style={{ ...s.td, textAlign: "center" }}>
                                                {order.trigger_count || 1}
                                            </td>
                                            <td style={{ ...s.td, color: "#64748b", fontSize: "12px" }}>
                                                {timeAgo(order.last_event_at)}
                                            </td>
                                            <td style={s.td} onClick={e => e.stopPropagation()}>
                                                {/* Fulfilled/Cancelled-in-Shopify with mismatch → Alert badge */}
                                                {(order.status === "fulfilled" || order.status === "cancelled_in_shopify") && order.location_mismatch && (
                                                    <span style={{
                                                        display: "inline-block",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "10px",
                                                        fontWeight: 700,
                                                        background: "#ef444420",
                                                        color: "#ef4444",
                                                    }}>
                                                        {"\u26A0"} Mismatch
                                                    </span>
                                                )}
                                                {/* Mismatch + SH Fulfilled → Alert only */}
                                                {order.status === "validated_mismatch" && (order.shiphero_status === "fulfilled" || order.shiphero_status === "cancelled") && (
                                                    <>
                                                        <span style={{
                                                            display: "inline-block",
                                                            padding: "2px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "10px",
                                                            fontWeight: 700,
                                                            background: "#f59e0b20",
                                                            color: "#f59e0b",
                                                            marginRight: "4px",
                                                        }}>
                                                            Mismatch — SH {order.shiphero_status}
                                                        </span>
                                                        <button
                                                            style={s.actionBtn("#94a3b8")}
                                                            onClick={() => onDismiss(order.id)}
                                                            disabled={actionLoading === order.id}
                                                        >
                                                            Dismiss
                                                        </button>
                                                    </>
                                                )}
                                                {/* Mismatch + SH not fulfilled → Cancel + Dismiss */}
                                                {order.status === "validated_mismatch" && order.shiphero_status !== "fulfilled" && order.shiphero_status !== "cancelled" && (
                                                    <>
                                                        <button
                                                            style={s.actionBtn("#ef4444")}
                                                            onClick={() => onExecute(order.id)}
                                                            disabled={actionLoading === order.id}
                                                        >
                                                            {actionLoading === order.id ? "..." : "Cancel Items"}
                                                        </button>
                                                        <button
                                                            style={s.actionBtn("#94a3b8")}
                                                            onClick={() => onDismiss(order.id)}
                                                            disabled={actionLoading === order.id}
                                                        >
                                                            Dismiss
                                                        </button>
                                                    </>
                                                )}
                                                {/* Failed → Retry Action + Re-validate */}
                                                {order.status === "action_failed" && (
                                                    <>
                                                        <button
                                                            style={s.actionBtn("#f59e0b")}
                                                            onClick={() => onExecute(order.id)}
                                                            disabled={actionLoading === order.id}
                                                        >
                                                            {actionLoading === order.id ? "..." : "Retry Action"}
                                                        </button>
                                                        <button
                                                            style={s.actionBtn("#06b6d4")}
                                                            onClick={() => onRevalidate(order.id)}
                                                            disabled={actionLoading === order.id}
                                                        >
                                                            Re-validate
                                                        </button>
                                                    </>
                                                )}
                                                {/* OK / Pending → Re-validate */}
                                                {["validated_ok", "event_received", "queued"].includes(order.status) && (
                                                    <button
                                                        style={s.actionBtn("#06b6d4")}
                                                        onClick={() => onRevalidate(order.id)}
                                                        disabled={actionLoading === order.id}
                                                    >
                                                        Re-validate
                                                    </button>
                                                )}
                                                {/* OK → Dismiss */}
                                                {order.status === "validated_ok" && (
                                                    <button
                                                        style={s.actionBtn("#94a3b8")}
                                                        onClick={() => onDismiss(order.id)}
                                                        disabled={actionLoading === order.id}
                                                    >
                                                        Dismiss
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Inline action error for this order */}
                                        {hasInlineError && (
                                            <tr>
                                                <td colSpan={10}>
                                                    <div style={s.inlineError}>
                                                        {actionError.action} failed: {actionError.message}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {/* Alert note for fulfilled-in-SH mismatch */}
                                        {order.notes && order.notes.startsWith("ALERT:") && (
                                            <tr>
                                                <td colSpan={10}>
                                                    <div style={{
                                                        padding: "6px 16px",
                                                        background: "rgba(245, 158, 11, 0.10)",
                                                        borderBottom: "1px solid #f59e0b33",
                                                        color: "#fbbf24",
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}>
                                                        <span style={{ fontSize: "14px" }}>{"\u26A0\uFE0F"}</span>
                                                        {order.notes}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {/* Expanded detail panel */}
                                        {expanded && (
                                            <tr>
                                                <td colSpan={10} style={s.expandRow}>
                                                    <OrderDetailPanel order={order} />
                                                    {lineItems.length > 0 && (
                                                        <div style={{ padding: "0 20px 16px" }}>
                                                            <div style={{
                                                                fontSize: "11px", fontWeight: 700, color: "#8b5cf6",
                                                                textTransform: "uppercase", letterSpacing: "0.5px",
                                                                marginBottom: "6px",
                                                            }}>
                                                                Line Items ({lineItems.length})
                                                            </div>
                                                            <LineItemSubTable
                                                                lineItems={lineItems}
                                                                cancelledIds={order.cancelled_line_item_ids}
                                                                orderStatus={order.status}
                                                            />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    {pages > 1 && (
                        <div style={s.pagination}>
                            <button
                                style={s.pageBtn(page <= 1)}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                            >
                                Prev
                            </button>
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>
                                Page {page} of {pages}
                            </span>
                            <button
                                style={s.pageBtn(page >= pages)}
                                onClick={() => setPage(p => Math.min(pages, p + 1))}
                                disabled={page >= pages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
